import { useState } from "react";

import {
  DEFAULT_DECAY_ASSIGNMENTS,
  DEFAULT_DECAY_PROFILES,
} from "../constants/decay";
import { DecayAssignments, DecayPeriod, DecayProfile } from "../types/decay";
import type { SandboxEntityType } from "../types/entities";
import type { Interaction, InteractionType } from "../types/interactions";

// ─── Storage helpers ──────────────────────────────────────────────────────────

const PROFILES_KEY = "sandbox_decay_profiles";
const ASSIGNMENTS_KEY = "sandbox_decay_assignments";

const loadProfiles = (): DecayProfile[] => {
  try {
    const raw = localStorage.getItem(PROFILES_KEY);
    if (!raw) return structuredClone(DEFAULT_DECAY_PROFILES);
    const saved = JSON.parse(raw) as DecayProfile[];
    // Ensure built-in default profiles are always present
    const savedIds = new Set(saved.map((p) => p.id));
    const missing = DEFAULT_DECAY_PROFILES.filter((p) => !savedIds.has(p.id));
    return [...saved, ...missing];
  } catch {
    return structuredClone(DEFAULT_DECAY_PROFILES);
  }
};

const loadAssignments = (): DecayAssignments => {
  try {
    const raw = localStorage.getItem(ASSIGNMENTS_KEY);
    return raw
      ? (JSON.parse(raw) as DecayAssignments)
      : structuredClone(DEFAULT_DECAY_ASSIGNMENTS);
  } catch {
    return structuredClone(DEFAULT_DECAY_ASSIGNMENTS);
  }
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
const useSandboxDecay = () => {
  const [profiles, setProfiles] = useState<DecayProfile[]>(() =>
    loadProfiles(),
  );
  const [assignments, setAssignments] = useState<DecayAssignments>(() =>
    loadAssignments(),
  );

  const getDefaultProfileId = (
    entityType: SandboxEntityType,
    interactionType: InteractionType,
  ): string | null => {
    return assignments[`${entityType}:${interactionType}`] ?? null;
  };

  const setAssignment = (
    entityType: SandboxEntityType,
    interactionType: InteractionType,
    profileId: string | null,
  ): void => {
    setAssignments((prev) => {
      const next = structuredClone(prev);
      next[`${entityType}:${interactionType}`] = profileId;
      localStorage.setItem(ASSIGNMENTS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const addProfile = (): void => {
    const profile: DecayProfile = {
      id: crypto.randomUUID(),
      name: "New profile",
      q1_days: 30,
      q1_multiplier: 1.0,
      q2_days: 60,
      q2_multiplier: 0.5,
      q3_days: 90,
      q3_multiplier: 0.25,
      limit_days: 120,
    };
    setProfiles((prev) => {
      const next = [...prev, profile];
      localStorage.setItem(PROFILES_KEY, JSON.stringify(next));
      return next;
    });
  };

  const updateProfile = (id: string, patch: Partial<DecayProfile>): void => {
    setProfiles((prev) => {
      const next = prev.map((p) => (p.id === id ? { ...p, ...patch } : p));
      localStorage.setItem(PROFILES_KEY, JSON.stringify(next));
      return next;
    });
  };

  const deleteProfile = (id: string): void => {
    setProfiles((prev) => {
      const next = prev.filter((p) => p.id !== id);
      localStorage.setItem(PROFILES_KEY, JSON.stringify(next));
      return next;
    });
    // Clear any assignments pointing at this profile
    setAssignments((prev) => {
      const next = structuredClone(prev);
      for (const key of Object.keys(next)) {
        if (next[key] === id) next[key] = null;
      }
      localStorage.setItem(ASSIGNMENTS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const resetToDefaults = (): void => {
    const freshProfiles = structuredClone(DEFAULT_DECAY_PROFILES);
    const freshAssignments = structuredClone(DEFAULT_DECAY_ASSIGNMENTS);
    localStorage.setItem(PROFILES_KEY, JSON.stringify(freshProfiles));
    localStorage.setItem(ASSIGNMENTS_KEY, JSON.stringify(freshAssignments));
    setProfiles(freshProfiles);
    setAssignments(freshAssignments);
  };

  const importConfig = (
    newProfiles: DecayProfile[],
    newAssignments: DecayAssignments,
  ): void => {
    const missing = DEFAULT_DECAY_PROFILES.filter(
      (p) => !newProfiles.some((np) => np.id === p.id),
    );
    const merged = [...newProfiles, ...missing];
    localStorage.setItem(PROFILES_KEY, JSON.stringify(merged));
    localStorage.setItem(ASSIGNMENTS_KEY, JSON.stringify(newAssignments));
    setProfiles(merged);
    setAssignments(newAssignments);
  };

  const getDecayMultiplier = (interaction: Interaction): number => {
    if (!interaction.decayPeriod) return 1.0;
    const profileId = getDefaultProfileId(
      interaction.entityType,
      interaction.interactionType,
    );
    if (!profileId) return 1.0;

    const profile = profiles.find((p) => p.id === profileId);
    if (!profile) return 1.0;

    const key = `${interaction.decayPeriod}_multiplier` as const;
    return profile[key];
  };

  return {
    profiles,
    assignments,
    getDefaultProfileId,
    setAssignment,
    addProfile,
    updateProfile,
    deleteProfile,
    resetToDefaults,
    getDecayMultiplier,
    importConfig,
  };
};

export default useSandboxDecay;
